<script setup lang="ts">
/**
 * Attach documents that are ALREADY in the library to the chat context (MAR-126).
 *
 * Until now the only ways in were drag-and-drop from the documents drawer and scoping to a case —
 * neither discoverable, and neither available when the drawer is collapsed. This is the explicit
 * path: the composer's paperclip opens it, the upload button is a separate control next to it.
 *
 * Mounted only while open (the host uses `v-if`), so the initial load runs in `onMounted` and no
 * watcher is needed. An empty query lists the 50 most recently touched items — `/api/search/items`
 * returns folders too, which are filtered out here: chat context is per-document.
 */
import { FileText, Check, Loader2 } from 'lucide-vue-next'
import type { ItemRow, ItemSearchPage } from '~~/shared/items/itemRow'
import SearchInput from '~/components/controls/SearchInput.vue'

const props = defineProps<{
  /** Ids already attached — shown as such and not selectable. */
  attachedIds: string[]
}>()

const emit = defineEmits<{
  close: []
  select: [docs: Array<{ id: string; name: string; file_name: string | null; status: string | null }>]
}>()

const { t } = useI18n()
const { apiFetch } = useApi()

const query = ref('')
const documents = ref<ItemRow[]>([])
const selectedIds = ref<Set<string>>(new Set())
const loading = ref(false)

let searchToken = 0

async function search() {
  const token = ++searchToken
  loading.value = true
  try {
    // `{ items, total }` since #169, NOT a bare array. Reading it as one threw a TypeError into
    // the catch below, which set `documents = []` — so every search rendered "no documents found"
    // with a 200 on the wire and nothing in the console (#229).
    const { items } = await apiFetch<ItemSearchPage>('/api/search/items', {
      method: 'POST',
      body: { query: query.value.trim() },
    })
    // A slower earlier request must not overwrite a newer one's results.
    if (token !== searchToken) return
    documents.value = items.filter(r => r.type === 'document')
  } catch {
    if (token === searchToken) documents.value = []
  } finally {
    if (token === searchToken) loading.value = false
  }
}

// Debounced so typing doesn't fire a request per keystroke.
let debounce: ReturnType<typeof setTimeout> | null = null
function onQueryInput() {
  if (debounce) clearTimeout(debounce)
  debounce = setTimeout(() => { void search() }, 200)
}
onUnmounted(() => { if (debounce) clearTimeout(debounce) })

onMounted(() => { void search() })

function isAttached(id: string) {
  return props.attachedIds.includes(id)
}

function toggle(id: string) {
  const next = new Set(selectedIds.value)
  if (next.has(id)) next.delete(id)
  else next.add(id)
  selectedIds.value = next
}

function confirm() {
  if (selectedIds.value.size === 0) return
  const picked = documents.value
    .filter(d => selectedIds.value.has(d.id))
    .map(d => ({
      id: d.id,
      name: d.name || d.file_name || d.id,
      file_name: d.file_name ?? null,
      status: d.status ?? null,
    }))
  emit('select', picked)
  emit('close')
}

function onOpenChange(open: boolean) {
  if (!open) emit('close')
}
</script>

<template>
  <Dialog :open="true" @update:open="onOpenChange">
    <DialogContent class="max-w-lg max-h-[80vh] flex flex-col">
      <DialogHeader>
        <DialogTitle>{{ t('chat.picker.title') }}</DialogTitle>
        <DialogDescription>{{ t('chat.picker.description') }}</DialogDescription>
      </DialogHeader>

      <SearchInput
        v-model="query"
        :placeholder="t('chat.picker.search')"
        autofocus
        @input="onQueryInput"
        @clear="search"
        @keydown.enter.prevent="search"
      />

      <div class="flex-1 min-h-0 overflow-y-auto border border-border rounded-lg divide-y divide-border">
        <div v-if="loading" class="flex items-center justify-center gap-2 p-6 text-sm text-muted-foreground">
          <Loader2 class="h-4 w-4 animate-spin" /> {{ t('chat.picker.searching') }}
        </div>
        <div v-else-if="documents.length === 0" class="p-6 text-center text-sm text-muted-foreground">
          {{ t('chat.picker.empty') }}
        </div>
        <template v-else>
          <button
            v-for="doc in documents"
            :key="doc.id"
            type="button"
            class="w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors"
            :class="isAttached(doc.id)
              ? 'opacity-50 cursor-not-allowed'
              : selectedIds.has(doc.id) ? 'bg-accent-surface cursor-pointer' : 'hover:bg-muted cursor-pointer'"
            :disabled="isAttached(doc.id)"
            @click="toggle(doc.id)"
          >
            <span
              class="h-5 w-5 shrink-0 rounded border flex items-center justify-center"
              :class="selectedIds.has(doc.id) ? 'bg-primary border-primary' : 'border-border'"
            >
              <Check v-if="selectedIds.has(doc.id)" class="h-3 w-3 text-primary-foreground" />
            </span>
            <FileText class="h-4 w-4 shrink-0 text-muted-foreground" />
            <span class="min-w-0 flex-1">
              <span class="block text-sm text-foreground truncate">{{ doc.name || doc.file_name }}</span>
              <span v-if="doc.file_name && doc.file_name !== doc.name" class="block text-xs text-muted-foreground truncate">
                {{ doc.file_name }}
              </span>
            </span>
            <span v-if="isAttached(doc.id)" class="shrink-0 text-xs text-muted-foreground">
              {{ t('chat.picker.alreadyAttached') }}
            </span>
          </button>
        </template>
      </div>

      <DialogFooter class="flex items-center justify-end gap-2">
        <Button variant="outline" @click="emit('close')">{{ t('common.cancel') }}</Button>
        <Button :disabled="selectedIds.size === 0" @click="confirm">
          {{ selectedIds.size ? t('chat.picker.attachCount', { count: selectedIds.size }) : t('chat.picker.attach') }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
